import { OverlayPanel } from "primereact/overlaypanel";
import { Fragment, ReactNode, useRef, useState } from "react";
import styled from "styled-components";

import { FilterPanelStyled } from "./styled";

const TriggerStyled = styled.button`
  cursor: pointer;
  padding: 0.25rem 0.5rem;

  border: none;
  border-radius: 8px;
  background: none;

  color: var(--brand-purple-700);
  font-size: 1rem;

  &:hover {
    background-color: color-mix(
      in srgb,
      var(--brand-purple-700) 8%,
      var(--brand-white)
    );
  }

  &:focus-visible {
    outline: 2px solid var(--brand-purple-600);
    outline-offset: 2px;
  }

  &[data-neutral="true"] {
    color: var(--brand-black);
  }

  &[data-neutral="true"]:hover {
    background-color: var(--brand-gray-100);
  }
`;

const MenuPanelStyled = styled(FilterPanelStyled)`
  .p-overlaypanel-content {
    min-width: 15rem;
    padding: 0.5rem;
  }
`;

export const MenuItemStyled = styled.button`
  width: 100%;
  cursor: pointer;
  padding: 0.625rem 0.75rem;

  border: none;
  border-radius: 12px;
  background: none;

  color: var(--brand-black);
  font-family: var(--font-family);
  font-size: 0.825rem;
  text-align: left;
  white-space: nowrap;

  &:disabled {
    cursor: default;
    color: var(--brand-gray-400);
  }

  &:hover:not(:disabled) {
    background-color: color-mix(
      in srgb,
      var(--brand-purple-700) 8%,
      var(--brand-white)
    );
    color: var(--brand-purple-700);
    font-weight: var(--font-weight-semibold);
  }

  &:focus-visible {
    outline: 2px solid var(--brand-purple-600);
    outline-offset: -2px;
  }

  &[data-neutral="true"]:hover:not(:disabled) {
    background-color: var(--brand-gray-100);
    color: var(--brand-black);
  }

  &[data-danger="true"]:not(:disabled) {
    color: var(--red-600);
  }

  &[data-danger="true"]:hover:not(:disabled) {
    background-color: color-mix(in srgb, var(--red-600) 8%, var(--brand-white));
    color: var(--red-600);
  }
`;

export interface IRowActionsMenuItem {
  label: string;
  icon?: string;
  // Rendered in place of the button, for an item that is not a plain button
  // (the order view's PDF download link). The element draws itself with
  // `MenuItemStyled`.
  element?: ReactNode;
  disabled?: boolean;
  // Red item for destructive actions, e.g. "Cancel Estimate".
  danger?: boolean;
  // Native tooltip, used to say why an item is disabled.
  title?: string;
  // Returning a promise holds the menu open with this item spinning until the
  // work settles. Without it the menu closes onto an unchanged row, which reads
  // as "nothing happened" — and a second impatient click lands on the row
  // underneath, whose double-click handler navigates away.
  onClick?: () => void | Promise<unknown>;
}

export function RowActionsMenu({
  items,
  testId,
  neutral,
}: {
  items: IRowActionsMenuItem[];
  testId?: string;
  // Black trigger and items (only danger items stay red); the default is the purple table look.
  neutral?: boolean;
}) {
  const panel = useRef<OverlayPanel>(null);
  const [pendingLabel, setPendingLabel] = useState<string | null>(null);

  // Nothing to offer, so no trigger either (the order view has no items on a canceled estimate).
  if (!items.length) return null;

  return (
    <>
      <TriggerStyled
        type="button"
        aria-label="Estimate actions"
        data-testid={testId}
        data-neutral={neutral ? "true" : undefined}
        onClick={(e) => panel.current?.toggle(e)}
      >
        <i className="pi pi-ellipsis-h" />
      </TriggerStyled>

      <MenuPanelStyled ref={panel}>
        {items.map((item) =>
          item.element ? (
            <Fragment key={item.label}>{item.element}</Fragment>
          ) : (
            <MenuItemStyled
              key={item.label}
              type="button"
              // Every item locks while one is in flight, so the menu cannot fire a
              // second action on top of the first.
              disabled={item.disabled || pendingLabel !== null}
              data-danger={item.danger ? "true" : undefined}
              data-neutral={neutral ? "true" : undefined}
              title={item.title}
              className="flex align-items-center gap-3"
              onClick={(e) => {
                // First, so a throwing handler cannot let the click reach the row.
                e.stopPropagation();
                const result = item.onClick?.();
                if (!(result instanceof Promise)) {
                  panel.current?.hide();
                  return;
                }
                setPendingLabel(item.label);
                // The handler owns reporting its own failure; this only has to
                // release the menu either way.
                result
                  .catch(() => undefined)
                  .then(() => {
                    setPendingLabel(null);
                    panel.current?.hide();
                  });
              }}
            >
              <i
                className={
                  pendingLabel === item.label
                    ? "pi pi-spin pi-spinner"
                    : item.icon
                }
              />
              {item.label}
            </MenuItemStyled>
          )
        )}
      </MenuPanelStyled>
    </>
  );
}
